import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Clock, Calendar, MapPin, CheckCircle2, LogIn, LogOut, XCircle, Star, Plus, Coins, Crown } from "lucide-react";
import { toast } from "sonner";

const statusColor = {
  confirmed: "bg-sage/15 text-sage", completed: "bg-charcoal/10 text-charcoal",
  cancelled: "bg-signal/10 text-signal", requested: "bg-sun/20 text-charcoal",
};

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [reviewFor, setReviewFor] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/bookings");
    setBookings(data.bookings);
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => { try { await fn(); await load(); await refresh(); toast.success(ok); } catch (e) { toast.error(formatError(e.response?.data?.detail)); } };
  const submitReview = async () => {
    try {
      await api.post("/reviews", { booking_id: reviewFor.id, rating, comment });
      toast.success("Thanks for your review!"); setReviewFor(null); setComment(""); setRating(5); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const credits = user?.time_credits_minutes || 0;
  const upcoming = bookings.filter((b) => b.status === "confirmed");
  const past = bookings.filter((b) => ["completed", "cancelled"].includes(b.status));

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-sand">
      <div className="max-w-5xl mx-auto px-5 md:px-8 py-10">
        <h1 className="font-display text-4xl md:text-5xl font-black text-charcoal">Hi, {user?.name?.split(" ")[0]} 👋</h1>
        <p className="text-charcoal/60 mt-2">Your hangouts, credits and reviews — all in one place.</p>

        {/* stat cards */}
        <div className="grid sm:grid-cols-3 gap-4 mt-8">
          <div className="bg-terracotta rounded-2xl p-6 text-white">
            <Coins className="w-6 h-6 mb-2" />
            <p className="font-display text-3xl font-bold">{credits}</p>
            <p className="text-sm text-white/80">minutes of credits</p>
            <Button data-testid="buy-credits" onClick={() => navigate("/packages")} className="mt-3 rounded-full bg-white text-terracotta hover:bg-white/90 h-9 text-sm"><Plus className="w-4 h-4 mr-1" /> Buy more</Button>
          </div>
          <div className="bg-white rounded-2xl border border-border p-6">
            <Calendar className="w-6 h-6 mb-2 text-sage" />
            <p className="font-display text-3xl font-bold text-charcoal">{upcoming.length}</p>
            <p className="text-sm text-charcoal/60">upcoming hangouts</p>
          </div>
          <div className="bg-white rounded-2xl border border-border p-6">
            <Crown className="w-6 h-6 mb-2 text-sun" />
            <p className="font-display text-xl font-bold text-charcoal mt-1">{user?.membership?.active ? "Member" : "Free plan"}</p>
            <p className="text-sm text-charcoal/60">{user?.kyc_status === "approved" ? "KYC verified ✓" : `KYC: ${user?.kyc_status}`}</p>
          </div>
        </div>

        {user?.kyc_status !== "approved" && (
          <div className="bg-sun/20 border border-sun rounded-2xl p-4 mt-6 flex items-center justify-between flex-wrap gap-3">
            <p className="text-sm text-charcoal">Finish verification to unlock full trust and visibility.</p>
            <Button onClick={() => navigate("/onboarding")} className="rounded-full bg-charcoal hover:bg-charcoal/90 text-white h-9 text-sm">Complete verification</Button>
          </div>
        )}

        {/* upcoming */}
        <h2 className="font-display text-2xl font-bold text-charcoal mt-10 mb-4">Upcoming</h2>
        {upcoming.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-border p-8 text-center">
            <p className="text-charcoal/50">No upcoming hangouts. <button onClick={() => navigate("/discover")} className="text-terracotta font-semibold">Find someone</button></p>
          </div>
        ) : upcoming.map((b) => (
          <BookingRow key={b.id} b={b} me={user.id} statusColor={statusColor}
            onCheckin={() => act(() => api.post(`/bookings/${b.id}/checkin`), "Checked in — stay safe!")}
            onCheckout={() => act(() => api.post(`/bookings/${b.id}/checkout`), "Session complete!")}
            onCancel={() => act(() => api.post(`/bookings/${b.id}/cancel`), "Booking cancelled")}
          />
        ))}

        {/* past */}
        {past.length > 0 && (<>
          <h2 className="font-display text-2xl font-bold text-charcoal mt-10 mb-4">Past</h2>
          {past.map((b) => (
            <div key={b.id} className="bg-white rounded-2xl border border-border p-5 mb-3 flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="flex items-center gap-2"><span className="font-display text-lg font-bold text-charcoal">{b.guest_id === user.id ? b.host_name : b.guest_name}</span>
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${statusColor[b.status]}`}>{b.status}</span></div>
                <p className="text-sm text-charcoal/60">{b.activity} · {b.venue}</p>
              </div>
              {b.status === "completed" && (
                <Button data-testid={`review-${b.id}`} variant="outline" onClick={() => setReviewFor(b)} className="rounded-full border-charcoal/20 text-sm"><Star className="w-4 h-4 mr-1" /> Review</Button>
              )}
            </div>
          ))}
        </>)}
      </div>

      <Dialog open={!!reviewFor} onOpenChange={(o) => !o && setReviewFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-2xl">Leave a review</DialogTitle></DialogHeader>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((s) => (
              <button key={s} data-testid={`star-${s}`} onClick={() => setRating(s)}><Star className={`w-8 h-8 ${s <= rating ? "fill-sun text-sun" : "text-charcoal/20"}`} /></button>
            ))}
          </div>
          <Textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="How was the hangout?" className="rounded-xl" />
          <DialogFooter><Button data-testid="review-submit" onClick={submitReview} className="rounded-full bg-terracotta hover:bg-terracotta/90 text-white">Submit</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BookingRow({ b, me, statusColor, onCheckin, onCheckout, onCancel }) {
  const other = b.guest_id === me ? b.host_name : b.guest_name;
  return (
    <div className="bg-white rounded-2xl border border-border p-5 mb-3">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-display text-xl font-bold text-charcoal">{other}</span>
            <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${statusColor[b.status]}`}>{b.status}</span>
            {b.is_first_meeting && <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-terracotta/10 text-terracotta">First meeting</span>}
          </div>
          <div className="flex flex-wrap gap-4 text-sm text-charcoal/60 mt-2">
            <span className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {new Date(b.start).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
            <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {b.duration_min / 60}h · {b.activity}</span>
            <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {b.venue}</span>
          </div>
        </div>
      </div>
      <div className="flex gap-2 mt-4 flex-wrap">
        {!b.checkin_at && <Button data-testid={`checkin-${b.id}`} onClick={onCheckin} className="rounded-full bg-sage hover:bg-sage/90 text-white h-9 text-sm"><LogIn className="w-4 h-4 mr-1" /> Check in</Button>}
        {b.checkin_at && !b.checkout_at && <Button data-testid={`checkout-${b.id}`} onClick={onCheckout} className="rounded-full bg-charcoal hover:bg-charcoal/90 text-white h-9 text-sm"><LogOut className="w-4 h-4 mr-1" /> Check out</Button>}
        <Button data-testid={`cancel-${b.id}`} variant="ghost" onClick={onCancel} className="rounded-full text-signal h-9 text-sm"><XCircle className="w-4 h-4 mr-1" /> Cancel</Button>
      </div>
    </div>
  );
}
